import { describeSourcePipelineStatus, describeSourceTypeLabel, MAX_ACTIVE_BUDDIES } from "./buddyGuaranteeShared.js";

// Prompt 140C: real chart components for the worker-to-worker Buddy
// network - a center node (the selected person) with up to
// MAX_ACTIVE_BUDDIES real buddy nodes around it, and a compact 0/5..5/5
// coverage distribution chart. Both are plain SVG/HTML (no charting
// library) - simple enough to render real data directly, honest about
// empty states, never a fake map/tracking visualization.

function initials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const NODE_RADIUS = 26;
const CENTER_RADIUS = 34;
const RING_RADIUS = 96;
const VIEWBOX_SIZE = 260;
const VIEWBOX_CENTER = VIEWBOX_SIZE / 2;

/**
 * One real person's network - `centerPerson` is the selected worker/
 * applicant ({ name, sourceType, pipelineStatus }), `buddies` is that
 * person's own real ACTIVE buddy list (already fetched, up to
 * MAX_ACTIVE_BUDDIES). Empty buddy slots render as dashed placeholder
 * rings so real remaining capacity is visible at a glance - never filled
 * with a fake node. No map, no live position, no tracking of any kind -
 * purely a relationship diagram over already-real data.
 */
export function BuddyNetworkChart({ centerPerson, buddies }) {
  const slots = Array.from({ length: MAX_ACTIVE_BUDDIES }, (_, index) => buddies[index] ?? null);
  const positions = slots.map((_, index) => {
    const angle = (Math.PI * 2 * index) / MAX_ACTIVE_BUDDIES - Math.PI / 2;
    return {
      x: VIEWBOX_CENTER + RING_RADIUS * Math.cos(angle),
      y: VIEWBOX_CENTER + RING_RADIUS * Math.sin(angle),
    };
  });

  const centerName = centerPerson?.name ?? "-";

  return (
    <div className="wx-bg-network">
      <svg
        className="wx-bg-network-svg"
        viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
        role="img"
        aria-label={`เครือข่าย Buddy ของ ${centerName}`}
      >
        {slots.map(
          (buddy, index) =>
            buddy && (
              <line
                key={`edge-${index}`}
                className="wx-bg-network-edge"
                x1={VIEWBOX_CENTER}
                y1={VIEWBOX_CENTER}
                x2={positions[index].x}
                y2={positions[index].y}
              />
            )
        )}

        {slots.map((buddy, index) =>
          buddy ? (
            <g
              key={`node-${index}`}
              className={`wx-bg-network-node wx-bg-network-node--${describeSourcePipelineStatus(buddy.sourceType, buddy.pipelineStatus).badgeVariant}`}
              transform={`translate(${positions[index].x}, ${positions[index].y})`}
            >
              <circle r={NODE_RADIUS} />
              <text textAnchor="middle" dominantBaseline="central">
                {initials(buddy.name)}
              </text>
            </g>
          ) : (
            <g key={`empty-${index}`} className="wx-bg-network-node wx-bg-network-node--empty" transform={`translate(${positions[index].x}, ${positions[index].y})`}>
              <circle r={NODE_RADIUS - 4} strokeDasharray="4 4" />
            </g>
          )
        )}

        <g className="wx-bg-network-node wx-bg-network-node--center" transform={`translate(${VIEWBOX_CENTER}, ${VIEWBOX_CENTER})`}>
          <circle r={CENTER_RADIUS} />
          <text textAnchor="middle" dominantBaseline="central">
            {initials(centerName)}
          </text>
        </g>
      </svg>

      <ul className="wx-bg-network-legend">
        <li className="wx-bg-network-legend-item wx-bg-network-legend-item--center">
          <span className="wx-bg-network-legend-dot wx-bg-network-legend-dot--center" aria-hidden="true" />
          <strong>{centerName}</strong>
          {centerPerson && <span className="wx-bg-network-legend-sub">{describeSourceTypeLabel(centerPerson.sourceType)}</span>}
        </li>
        {slots.map((buddy, index) => {
          if (!buddy) {
            return (
              <li key={`legend-empty-${index}`} className="wx-bg-network-legend-item wx-bg-network-legend-item--empty">
                <span className="wx-bg-network-legend-dot wx-bg-network-legend-dot--empty" aria-hidden="true" />
                <span>ว่าง</span>
              </li>
            );
          }
          const pipeline = describeSourcePipelineStatus(buddy.sourceType, buddy.pipelineStatus);
          return (
            <li key={`legend-${index}`} className="wx-bg-network-legend-item">
              <span className={`wx-bg-network-legend-dot wx-bg-network-legend-dot--${pipeline.badgeVariant}`} aria-hidden="true" />
              <strong>{buddy.name}</strong>
              <span className={`wx-badge wx-badge--${pipeline.badgeVariant}`}>{pipeline.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Sequential, single-hue (blue) ramp - light to dark - for a magnitude
// distribution across an ordered scale (0..5 buddies), never a rainbow of
// unrelated categorical colors. Reuses this app's own existing enterprise
// token ramp (src/theme/tokens.css) rather than inventing a new palette.
const DISTRIBUTION_RAMP = [
  "var(--wx-color-border-strong)",
  "var(--wx-color-primary-100)",
  "var(--wx-color-primary-400)",
  "var(--wx-color-primary-500)",
  "var(--wx-color-primary-600)",
  "var(--wx-color-primary-700)",
];

/** `buckets`: [{ bucket: 0..5, label: "0/5".."5/5", count }] from buildDistributionBuckets - real exact counts, bucket 0 the only derived one (see that function's own doc comment). */
export function BuddyDistributionChart({ buckets }) {
  const maxCount = Math.max(1, ...buckets.map((bucket) => bucket.count));
  const totalCount = buckets.reduce((sum, bucket) => sum + bucket.count, 0);

  if (totalCount === 0) {
    return <p className="wx-empty-list">ยังไม่มีข้อมูลสำหรับแสดงการกระจาย Buddy</p>;
  }

  return (
    <div className="wx-bg-dist-chart" role="img" aria-label="การกระจายจำนวน Buddy ต่อคน (0 ถึง 5)">
      {buckets.map((bucket, index) => (
        <div key={bucket.bucket} className="wx-bg-dist-col">
          <span className="wx-bg-dist-count">{bucket.count.toLocaleString("th-TH")}</span>
          <div className="wx-bg-dist-bar-track">
            <div
              className="wx-bg-dist-bar"
              style={{ height: `${Math.max(4, (bucket.count / maxCount) * 100)}%`, background: DISTRIBUTION_RAMP[index] }}
            />
          </div>
          <span className="wx-bg-dist-label">{bucket.label}</span>
        </div>
      ))}
    </div>
  );
}
